import { useCallback, useRef, useState } from 'react';

/**
 * Returns a wrapper that fires a brief "launch" animation on the clicked item
 * before calling the real onSelect handler.
 *
 * Usage:
 *   const { launchingId, launch } = useLaunchAnimation();
 *   <button data-launching={launchingId === id || undefined} onClick={() => launch(id, () => onSelect(result))} />
 *
 * The element with data-launching gets the CSS keyframe via its module's
 * [data-launching] selector. The animation takes ~220ms; onSelect fires
 * immediately so the tool activates without delay.
 */
export function useLaunchAnimation() {
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const launch = useCallback((id: string, onSelect: () => void) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setLaunchingId(id);
    onSelect(); // activate tool immediately — ghost pops up right away
    timerRef.current = setTimeout(() => setLaunchingId(null), 240);
  }, []);

  return { launchingId, launch };
}
